import { useCallback, useEffect, useRef, useState } from "react";

const QUESTION_PHASES = ["voting", "closed", "reveal", "learning"];

/**
 * Turns the live game's phase (set by the host) into JourneyScene animation phases — shared
 * by the players' screen (/vote) and the host's presenter view, so both boards move together.
 *
 * Returns the scene phase, a checkpoint key (bump = restart the animation) and the crossroads
 * the board shows, which lags behind `index` while the crowd walks on to the next one.
 */
export default function useLiveScene(live, phase, index) {
  const [scenePhase, setScenePhase] = useState("idle");
  const [checkpointKey, setCheckpointKey] = useState(0);
  const [sceneIndex, setSceneIndex] = useState(0);
  const seenRef = useRef({ phase: null, index: null, runId: null });

  useEffect(() => {
    if (live === undefined) return;
    const seen = seenRef.current;
    const runId = live?.runId || null;
    seenRef.current = { phase, index, runId };
    const sameRun = seen.runId === runId;
    const wasOnQuestion = sameRun && QUESTION_PHASES.includes(seen.phase);

    const walkingOn =
      (phase === "voting" && wasOnQuestion && seen.index !== index && index > seen.index) ||
      (phase === "complete" && wasOnQuestion);
    if (!walkingOn) setSceneIndex(index);
    if (phase === "lobby" || phase === "intro") setScenePhase("idle");
    else if (phase === "voting") {
      if (walkingOn) setScenePhase("merge"); // next crossroads released: walk on up the road
      else if (!(sameRun && seen.phase === "voting" && seen.index === index)) setScenePhase("approach");
      else return;
    } else if (phase === "closed") setScenePhase(sameRun && seen.phase === "voting" ? "travel" : "hold");
    else if (phase === "reveal" || phase === "learning") setScenePhase("hold");
    else if (phase === "complete") setScenePhase(wasOnQuestion ? "merge" : "done");
    else return;
    setCheckpointKey((k) => k + 1);
  }, [phase, index, live]);

  const onPhaseDone = useCallback(
    (done) => {
      if (done === "approach") setScenePhase(phase === "closed" ? "travel" : "decide");
      else if (done === "travel") setScenePhase("hold");
      else if (done === "merge") {
        setSceneIndex(index);
        setScenePhase(phase === "complete" ? "arrive" : "approach");
        setCheckpointKey((k) => k + 1);
      } else if (done === "arrive") setScenePhase("done");
    },
    [phase, index]
  );

  return { scenePhase, checkpointKey, sceneIndex, onPhaseDone };
}
